import { breakpointsUnits } from './breakpoints'
import { font } from 'settings/tokens'

/**
 * The app's type scale — five sizes, implementing `settings/tokens.ts`.
 *
 * ## What this replaces, and why it mattered
 *
 * The previous scale declared nine variants that resolved to **four sizes**:
 *
 *     h1 24 · h2 18 · h3 14 · h4 12 · h5 12
 *     subtitle1 14 · subtitle2 12 · body1 14 · body2 12
 *
 * So `h3`, `subtitle1`, `body1` and `body3` were all 14px, and `h4`, `h5`,
 * `subtitle2` and `body2` were all 12px. Six of the nine steps were
 * indistinguishable by SIZE and differed only by weight — which is the
 * mechanical reason the app read as one flat wall of text rather than as a
 * hierarchy. A reader resolves size long before they resolve weight.
 *
 * It also inverted at the `sm` breakpoint. The old responsive overrides sent
 * `h1 → 26`, `h2 → 30`, `h3 → 36`, so on a tablet the heading order ran
 * BACKWARDS: h3 larger than h2 larger than h1. That was a live bug, not
 * untidiness.
 *
 * Two headings (`h3`, `h4`) declared no `lineHeight` at all, and those that did
 * ranged from 1.17 to 1.67 with no pattern.
 *
 * ## The scale
 *
 * 32 / 24 / 20 / 16 / 14, with real jumps between steps. 14 is the floor:
 * 11–13px is not readable at arm's length on the phone this app is reached
 * from by NFC tap, and 16 is iOS's no-zoom minimum for anything focusable.
 *
 * **Line-height falls as size rises** — display type needs proportionally less
 * leading than body copy, so a single ratio across the scale makes headings
 * look airy and body copy look cramped. Body sits at 1.5, display at 1.2.
 *
 * **Negative tracking on the display sizes** is what a proportional webfont
 * needs and does not get for free. iOS's SF Pro is optically sized — it
 * switches design and tightens tracking above 20pt automatically — which is a
 * large part of why native iOS type looks tighter at headline sizes than a
 * Google font set at the same px. Lato has one design serving 12px and 32px,
 * so the tightening has to be applied here.
 *
 * Responsive bumps preserve ORDER at every breakpoint. That is the invariant
 * the old scale broke; keep h1 > h2 > h3 at both widths or don't bump at all.
 */
export const typography = (bodyFont: string, titleFont?: string) => ({
  fontFamily: bodyFont,
  // Custom: heading/title font (VERA-270). Read by the MuiTypography heading
  // overrides so titles can use a different font than the body.
  titleFontFamily: titleFont || bodyFont,

  /** Display — page titles. */
  h1: {
    fontSize: font.display,
    lineHeight: 1.2,
    fontWeight: 700,
    letterSpacing: '-0.02em',
    [`@media(min-width:${breakpointsUnits.sm}px)`]: {
      fontSize: 40,
    },
  },
  /** Title — major section headings. */
  h2: {
    fontSize: font.title,
    lineHeight: 1.25,
    fontWeight: 600,
    letterSpacing: '-0.015em',
    [`@media(min-width:${breakpointsUnits.sm}px)`]: {
      fontSize: 28,
    },
  },
  /** Heading — sub-sections, drawer titles. */
  h3: {
    fontSize: font.heading,
    lineHeight: 1.3,
    fontWeight: 600,
    letterSpacing: '-0.01em',
    [`@media(min-width:${breakpointsUnits.sm}px)`]: {
      fontSize: 22,
    },
  },
  /** Body-sized heading — a card or row title. No bump; it is already body size. */
  h4: {
    fontSize: font.body,
    lineHeight: 1.4,
    fontWeight: 600,
  },
  /** Label-sized heading — the small group caption over a list. */
  h5: {
    fontSize: font.label,
    lineHeight: 1.4,
    fontWeight: 600,
  },

  subtitle1: {
    fontSize: font.body,
    lineHeight: 1.5,
    fontWeight: 600,
  },
  subtitle2: {
    fontSize: font.label,
    lineHeight: 1.45,
    fontWeight: 600,
  },

  /**
   * Body is 16, not the old 14.
   *
   * This is also what the app ALREADY renders: a census of the running
   * certificates page found 16px on ~1099 text nodes against 14px on 25,
   * because components were overriding the theme's 14 inline. The theme was
   * simply lying about the app's own body size.
   */
  body1: {
    fontSize: font.body,
    lineHeight: 1.5,
  },
  /** Secondary/supporting copy. Was 12 — below the readable floor. */
  body2: {
    fontSize: font.label,
    lineHeight: 1.45,
  },
  /**
   * VERA-842: `body3` is the supporting-text size and was MISSING from this
   * theme, so any `variant="body3"` had no size at all and fell back to an
   * inline <span>.
   */
  body3: {
    fontSize: font.label,
    lineHeight: 1.45,
  },
  a: {
    fontFamily: bodyFont,
  },
})
